import { ArrowUUpLeft, Check, PencilSimple, X } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ConversationMessage } from "~/api/conversationTypes";
import { MessageMarkdown } from "~/features/conversations/MessageMarkdown";
import { stripSkills } from "~/features/conversations/skills/parseSkills";
import { Button } from "~/foundation/components/Button";
import { IconButton } from "~/foundation/components/IconButton";
import { Text } from "~/foundation/components/Text";
import { Tooltip } from "~/foundation/components/Tooltip";
import "~/features/work/work.css";
import { cn } from "~/foundation/utils/cn";

/** Bodies longer than this collapse behind "Show more" — kms's number. */
const PREVIEW_LENGTH = 150;

/** The review slider settles 500 ms after the last drag, then writes one `moderator_review`. */
const REVIEW_DEBOUNCE_MS = 500;

export interface MessageActions {
  /** Own message, or `documentUpdate` — slate's gate is the only one; the server has none. */
  canEdit: boolean;
  canReview: boolean;
  onRevise: (messageId: string, content: string) => Promise<void>;
  /** "Rewrite from here": revise the anchor and drop everything downstream, reversibly. */
  onRewriteFrom: (messageId: string, content: string) => Promise<void>;
  onReview: (message: ConversationMessage, rating: number, selection: string) => void;
  onHide: (messageId: string) => void;
}

function RoleBadge({ message }: { message: ConversationMessage }) {
  const { t } = useTranslation();
  if (message.role === "moderator") {
    const merged = message.metadata.kind === "ai" ? message.metadata.mergedFrom.length : 0;
    return (
      <span className="rounded-sm bg-accent-subtle px-1.5 py-0.5 text-caption text-accent">
        {merged > 0 ? t("Moderator · merged {{count}}", { count: merged }) : t("Moderator")}
      </span>
    );
  }
  if (message.authorType === "ai") {
    return (
      <span className="rounded-sm bg-bg-secondary px-1.5 py-0.5 text-caption text-text-tertiary">
        {t("AI")}
      </span>
    );
  }
  return null;
}

function Usage({ message }: { message: ConversationMessage }) {
  const { t } = useTranslation();
  if (message.metadata.kind !== "ai" || message.metadata.tokensUsed === null) {
    return null;
  }
  return (
    <Tooltip content={t("AI usage: {{tokens}} tokens", { tokens: message.metadata.tokensUsed })}>
      <span className="text-caption text-text-tertiary">
        {t("{{tokens}} tok", { tokens: message.metadata.tokensUsed })}
      </span>
    </Tooltip>
  );
}

/**
 * One turn.
 *
 * The two message actions kms shipped as `// TODO: Call API` toasts are implemented here as
 * the write they always should have been (a `moderator_review` row) — accepting and rejecting
 * a response now means something to the aggregation instead of showing a message that lies
 * (W8-16).
 */
export function MessageItem({
  message,
  actions,
  hidden,
}: {
  message: ConversationMessage;
  actions: MessageActions;
  hidden: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);
  const [saving, setSaving] = useState(false);
  const [rating, setRating] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (editing) {
      editorRef.current?.focus();
    }
  }, [editing]);

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  if (hidden) {
    return null;
  }

  /**
   * S7 — the skill fences are rendered as CARDS below the message (`messageExtras`), so the
   * prose must not repeat them. kms showed both: the raw ```skill:…``` block AND the card,
   * which is how a reader ended up scrolling past the same instruction twice.
   */
  const prose = stripSkills(message.content) || message.content;
  const long = prose.length > PREVIEW_LENGTH;
  const body = long && !expanded ? `${prose.slice(0, PREVIEW_LENGTH)}…` : prose;
  const mine = message.authorType === "human";

  const queueReview = (next: number) => {
    setRating(next);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      actions.onReview(message, next, message.content.slice(0, 120));
    }, REVIEW_DEBOUNCE_MS);
  };

  const save = async (rewrite: boolean) => {
    setSaving(true);
    try {
      if (rewrite) {
        await actions.onRewriteFrom(message.id, draft);
      } else {
        await actions.onRevise(message.id, draft);
      }
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <article
      className={cn(
        mine ? "docit-human-text" : "docit-ai-text",
        "flex flex-col gap-1.5 rounded-md border p-2.5",
        mine ? "border-divider bg-bg" : "border-divider bg-bg-secondary",
        message.flags.isStale ? "opacity-80" : null,
      )}
    >
      <header className="flex flex-wrap items-center gap-2">
        <Text variant="small" className="font-medium text-text">
          {message.author || (mine ? t("You") : t("Assistant"))}
        </Text>
        <RoleBadge message={message} />
        {message.isEdited ? (
          <span className="text-caption text-text-tertiary">{t("Edited")}</span>
        ) : null}
        {message.flags.isStale ? (
          <Tooltip content={t("An earlier message was edited after this answer.")}>
            <span className="rounded-sm bg-warning-subtle px-1.5 py-0.5 text-caption text-warning">
              {t("Stale")}
            </span>
          </Tooltip>
        ) : null}
        <span className="ms-auto flex items-center gap-2">
          <Usage message={message} />
          {actions.canEdit ? (
            <IconButton
              icon={PencilSimple}
              aria-label={t("Edit message")}
              onClick={() => {
                setDraft(message.content);
                setEditing(true);
              }}
            />
          ) : null}
          {!mine && actions.canReview ? (
            <>
              <IconButton
                icon={Check}
                aria-label={t("Accept this response")}
                onClick={() => queueReview(1)}
              />
              <IconButton
                icon={X}
                aria-label={t("Reject this response")}
                onClick={() => {
                  queueReview(-1);
                  actions.onHide(message.id);
                }}
              />
            </>
          ) : null}
        </span>
      </header>

      {editing ? (
        <div className="flex flex-col gap-2">
          <textarea
            ref={editorRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={5}
            aria-label={t("Edit message")}
            className="w-full rounded-sm border border-input-border bg-bg p-2 text-body text-text focus:outline-none"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" disabled={saving} onClick={() => void save(false)}>
              {saving ? t("Saving…") : t("Save")}
            </Button>
            <Tooltip content={t("Rewrites this message and drops every answer after it.")}>
              <Button size="sm" variant="neutral" disabled={saving} onClick={() => void save(true)}>
                <ArrowUUpLeft size={14} aria-hidden />
                {t("Rewrite from here")}
              </Button>
            </Tooltip>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              {t("Cancel")}
            </Button>
          </div>
        </div>
      ) : (
        <MessageMarkdown content={body} />
      )}

      {long && !editing ? (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="self-start text-caption text-link hover:underline"
        >
          {expanded ? t("Show less") : t("Show more")}
        </button>
      ) : null}

      {!mine && actions.canReview && !editing ? (
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={() => setReviewOpen((value) => !value)}
            className="self-start text-caption text-text-tertiary hover:text-text"
          >
            {reviewOpen ? t("Close") : t("Review this answer")}
          </button>
          {reviewOpen ? (
            <div className="flex items-center gap-2">
              <span className="text-caption text-text-tertiary">{t("Negative")}</span>
              <input
                type="range"
                min={-1}
                max={1}
                step={0.1}
                value={rating}
                aria-label={t("Review rating")}
                onChange={(event) => queueReview(Number(event.target.value))}
                className="flex-1"
              />
              <span className="text-caption text-text-tertiary">{t("Positive")}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
